/**
 * Authentication utility functions
 * Handles authentication errors and redirects to login when token is invalid
 */

import toast from "react-hot-toast";
import { UserLogoutAction } from "../Store/Actions/AuthActions";

/**
 * Checks if an error response indicates an invalid/expired token
 * @param {Object} result - API response result
 * @param {number} httpStatus - HTTP status code
 * @returns {boolean} - True if the error indicates invalid token
 */
export const isInvalidTokenError = (result, httpStatus) => {
  // Check HTTP status code
  if (httpStatus === 401) {
    return true;
  }

  // Check statusCode in response body
  if (result?.statusCode === 401) {
    return true;
  }

  // Check error message for token-related errors
  const errorMessage = result?.message || "";
  const messageStr = Array.isArray(errorMessage) 
    ? errorMessage.join(" ").toLowerCase()
    : String(errorMessage).toLowerCase();

  const invalidTokenKeywords = [
    "invalid token",
    "missing token",
    "token expired",
    "unauthorized",
    "invalid credentials",
    "authentication required",
    "token is invalid",
    "token has expired",
  ];

  return invalidTokenKeywords.some((keyword) => messageStr.includes(keyword));
};

/**
 * Handles authentication errors by clearing session and redirecting to login
 * @param {Object} result - API response result
 * @param {number} httpStatus - HTTP status code (optional)
 * @param {Function} dispatch - Redux dispatch function (optional)
 */
export const handleAuthError = (result = {}, httpStatus = null, dispatch = null) => {
  // Check both the provided status and result statusCode
  const statusCode = httpStatus || result?.statusCode;
  
  if (isInvalidTokenError(result, statusCode)) {
    // Clear localStorage
    localStorage.removeItem("loginSuccess");
    localStorage.removeItem("accessToken");

    // Dispatch logout action if dispatch is provided
    if (dispatch) {
      dispatch(UserLogoutAction());
    }

    // Show toast message
    const errorMessage = Array.isArray(result?.message) 
      ? result.message[0] 
      : result?.message || "Session expired. Please sign in again.";
    
    toast.error(errorMessage);

    // Redirect to login page
    setTimeout(() => {
      window.location.href = "/";
    }, 1000); // Small delay to allow toast to be visible

    return true; // Indicates auth error was handled
  }

  return false; // Not an auth error
};

